/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/utils/constants.ts
 *
 * Rôle :
 *   Constantes globales de l'application, utilisées comme fallback
 *   quand le backend (tne_site_settings) ne répond pas, ou comme
 *   « source de vérité » build-time pour les valeurs réellement figées
 *   (date d'événement, fuseau, fenêtres temporelles).
 *
 *   La règle est : si une valeur est aussi dans `tne_site_settings`,
 *   le hook (ex. `useEventPhase`) lit en priorité la BDD, et utilise
 *   ces constantes comme fallback gracieux. Cohérent avec §18 offline-first
 *   et §10.10 fail-safe.
 *
 * Réutilisable :
 *   100 %. Pour un autre salon DRANE : ajuster `DEFAULT_EVENT_*`
 *   (date, fin, fuseau). Le pattern reste identique. Aucune logique
 *   métier dans ce fichier, uniquement des constantes typées.
 *
 * Voir : CLAUDE.md §F12 (mode avant/pendant/après), §14.0 métadonnées
 *        événement, V1 SQL `tne_settings.event_*`, V11 SQL
 *        `tne_site_settings.event.*`.
 *
 * @author Mika
 * @since 2026-05-06
 */

// =============================================================================
// Date / heure de l'événement TNE 2026
// =============================================================================

/**
 * Date et heure de **début** de l'événement (ISO 8601 avec offset).
 * Source officielle CLAUDE.md §0 : « 27 mai 2026, 9h–17h, Centre des
 * Congrès, Épinal ». Offset `+02:00` = heure d'été d'Europe/Paris
 * (DST en mai).
 *
 * Utilisé par `useEventPhase` comme fallback si l'endpoint
 * `/api/me/site-settings.php?keys=event.date_iso` est indisponible
 * (offline, BDD HS, endpoint non encore livré).
 */
export const DEFAULT_EVENT_START = '2026-05-27T09:00:00+02:00';

/**
 * Date et heure de **fin** de l'événement (ISO 8601 avec offset).
 * Utilisé par `useEventPhase` comme fallback (même règle que
 * `DEFAULT_EVENT_START`).
 */
export const DEFAULT_EVENT_END = '2026-05-27T17:00:00+02:00';

/**
 * Fuseau horaire IANA de l'événement. Utilisé pour les affichages
 * de date/heure côté UI (ex. « 14 h 30 (heure de Paris) »).
 */
export const DEFAULT_EVENT_TZ = 'Europe/Paris';

/**
 * Nombre de jours avant l'événement où la phase « before » devient
 * pertinente (purement informatif, ex. afficher un compte à rebours).
 * 30 j est cohérent avec le seed `event.before_window_days` de V11.
 */
export const DEFAULT_EVENT_BEFORE_WINDOW_DAYS = 30;

// =============================================================================
// localStorage / overrides, usage interne (dev + démo DRANE)
// =============================================================================

/**
 * Clé localStorage permettant à l'équipe DRANE (ou Mika en démo)
 * de **forcer** la phase event affichée, sans toucher à la BDD ni à
 * l'horloge système.
 *
 * Valeurs autorisées : `'before' | 'during' | 'after'`.
 *
 * Usage :
 *   ```js
 *   localStorage.setItem('tne.dev.eventPhaseOverride', 'during');
 *   // → l'app se comporte comme si on était le 27 mai entre 9h et 17h.
 *   localStorage.removeItem('tne.dev.eventPhaseOverride');
 *   // → retour au comportement normal.
 *   ```
 *
 * Garde-fou : l'override est ignoré en production sauf si l'URL
 * contient `?devPhase=...` (cf. `useEventPhase` §F12).
 */
export const EVENT_PHASE_OVERRIDE_STORAGE_KEY = 'tne.dev.eventPhaseOverride';

/**
 * Nom du paramètre URL utilisé pour forcer la phase event en démo
 * (ex. `?devPhase=during`). Disponible dans toutes les phases
 * (dev ET prod), c'est volontaire pour permettre des démos DRANE
 * sans rebuild.
 */
export const EVENT_PHASE_OVERRIDE_URL_PARAM = 'devPhase';

/**
 * NOLAS 2026 : Clé localStorage du mode « Aperçu intégral admin » (2026-06-23).
 *
 * Quand ce flag vaut '1', `useEventPhase` force la **phase effective** à
 * `during` pour CE navigateur uniquement, ce qui **déverrouille tous les
 * écrans gardés par la phase** (scanner exposant, flux live webtv/webradio,
 * carte temps réel, etc.) SANS toucher la vraie date ni l'override démo.
 *
 * Différence avec le sélecteur démo (`EVENT_PHASE_OVERRIDE_STORAGE_KEY`) :
 *   - Le sélecteur force UNE phase précise (avant/pendant/après) pour tester
 *     le rendu de cette phase.
 *   - L'aperçu intégral dit « montre-moi l'expérience LIVE complète, peu
 *     importe la date » → pratique pour qu'un admin voie tout sans attendre
 *     le jour J et sans jongler avec le sélecteur.
 *
 * Portée = un navigateur (réglé depuis le dashboard admin, RoleGuard). Aucun
 * impact sécurité : ne fait que ré-afficher de l'UI gardée par la phase ; les
 * endpoints backend gardent leurs propres contrôles d'accès.
 */
export const ADMIN_PHASE_REVEAL_STORAGE_KEY = 'nolas.admin.phaseRevealAll';

// ─────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//   NOLAS n'avait pas de notion de « phase événement », toutes les pages
//   restaient identiques avant/pendant/après. En portant ces constantes
//   dans NOLAS, on peut activer un comportement « post-événement » qui
//   archive les KPIs et redirige le badge vers une page rétrospective.
// ─────────────────────────────────────────────────────────────────────────
